import React, { useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    useMediaQuery,
    Divider,
    useTheme,
} from "@mui/material";
import AndroidIcon from "@mui/icons-material/Android";
import Aos from "aos";
import { motion } from "framer-motion";

const screenshots = [
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/login?updatedAt=1758035419658",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/homeScr?updatedAt=1761221200413",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/invest?updatedAt=1756998051665",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/insurance?updatedAt=1758035402256",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/calcu?updatedAt=1756998051637",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/profile?updatedAt=1756998051689",
];

const AppPreview = () => {
    const isDesktop = useMediaQuery("(min-width:1024px)");
    const theme = useTheme();
    const MotionButton = motion(Button);

    const cardWidth = 180;
    const visiblePart = 0.7;

    useEffect(() => {
        Aos.init({ duration: 1000, once: false });
    }, []);

    return (
        <Box
            sx={{
                position: "relative",

                minHeight: "auto",
                overflow: "hidden",
            }}
        >
            {/* APP PREVIEW */}
            {isDesktop && (
                <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{
                        fontSize: 14, letterSpacing: 2, fontWeight: 600, color: theme.palette.primary.main,

                    }}
                        data-aos="fade-right">
                        • APP EXPERIENCE
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: "20px", sm: "28px", md: "40px" },
                            fontWeight: 600,
                            mb: 2,
                            color: theme.palette.text.primary,
                            position: "relative",
                            display: "inline-block",
                        }}
                    >
                        Experience{" "}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 900,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                position: "relative",
                                display: "inline-block",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: "-4px",
                                    left: 0,
                                    width: "100%",
                                    height: "2px",
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                                }
                            }}
                        >
                            ArthGyan
                        </Box>
                    </Typography>

                    <Typography
                        variant="body1"

                        sx={{
                            fontSize: "16px",

                            color: "#555",
                            maxWidth: 620,
                            mx: "auto",
                            mb: 14,
                        }}
                    >
                        Manage investments, insurance, and financial planning — all in one beautifully designed app.
                    </Typography>

                    {/* CARD SHOWCASE */}
                    <Box
                        sx={{
                            position: "relative",
                            width: `${cardWidth + (screenshots.length - 1) * cardWidth * visiblePart}px`,
                            mx: "auto",
                            pb: 50
                        }}
                    >
                        {screenshots.map((src, index) => {
                            const total = screenshots.length;
                            const centerIndex = Math.floor(total / 2);
                            const maxAngle = 50;
                            const step = (2 * maxAngle) / (total - 1);
                            const angle = -maxAngle + index * step;
                            const distanceFromCenter = Math.abs(index - centerIndex);
                            const zIndex = 100 - distanceFromCenter;

                            return (
                                <Box
                                    key={index}
                                    component="img"
                                    src={src}
                                    alt={`Screenshot ${index + 1}`}
                                    sx={{
                                        position: "absolute",
                                        bottom: 60,
                                        left: `calc(50% + ${(index - centerIndex) * 50}px - ${cardWidth / 2}px)`,
                                        width: `${cardWidth}px`,
                                        borderRadius: "20px",
                                        boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                                        transform: `rotate(${angle}deg)`,
                                        transformOrigin: "bottom center",
                                        zIndex,
                                        transition: "all 0.4s ease",
                                        "&:hover": {
                                            transform: `rotate(${angle}deg) scale(1.1)`,
                                            zIndex: 999,
                                        },
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* DOWNLOAD CTA */}
            <Box
                sx={{
                    py: { xs: 6, md: 2 },
                    textAlign: "center",

                }}
            >

                <Typography sx={{
                    fontSize: 13, letterSpacing: 2, fontWeight: 600, color: theme.palette.primary.main,
                }} data-aos="fade-right">
                    • GET STARTED
                </Typography>

                <Typography
                    sx={{
                        fontSize: { xs: "20px", sm: "28px", md: "40px" },
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        position: "relative",
                        display: "inline-block",
                    }}
                >
                    Download{" "}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 900,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            position: "relative",
                            display: "inline-block",
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                bottom: "-4px",
                                left: 0,
                                width: "100%",
                                height: "2px",
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                            }
                        }}
                    >
                        ArthGyan
                    </Box>{" "}
                    Today
                </Typography>


                <Typography
                    variant="body1"

                    sx={{
                        fontSize: { xs: "12px", sm: "14px", md: "16px" },
                        mt: 1,
                        color: "#555",
                        maxWidth: 600,
                        mx: "auto",
                        mb: 4,
                    }}
                >
                    Smart finance, simplified. Start your journey with ArthGyan today.
                </Typography>

                <Stack direction="row" justifyContent="center">
                    <MotionButton
                        whileHover={{ scale: 1.05 }}
                        startIcon={<AndroidIcon />}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: "8px",
                            fontWeight: 600,
                            backgroundColor: "#0080a7",
                            color: "#fff",
                            textTransform: "none",
                            boxShadow: `0 6px 18px ${theme.palette.primary.main}55`,
                            "&:hover": {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                        onClick={() =>
                            window.open(
                                "https://play.google.com/store/apps/details?id=com.arthgyan.financemanager",
                                "_blank"
                            )
                        }
                    >
                        Get it on Play Store
                    </MotionButton>
                </Stack>
            </Box>
        </Box >
    );
};

export default AppPreview;
